import React from 'react'
import PropTypes from 'prop-types'
import { useFormInput } from './FormInput.logic'
import { FormInputView } from './FormInput.view'

/**
 * FormInput Container Component
 * Text input field integrated with React Hook Form
 *
 * @example
 * <FormInput
 *   name="referenceNumber"
 *   control={control}
 *   label="Reference Number"
 *   placeholder="Enter reference number"
 *   required
 * />
 *
 * @example
 * // With number formatting (thousand separators)
 * <FormInput
 *   name="weight"
 *   control={control}
 *   label="Weight (lbs)"
 *   type="number"
 *   displayFormat="number"
 * />
 *
 * @example
 * // With phone masking
 * <FormInput
 *   name="phone"
 *   control={control}
 *   label="Contact Phone"
 *   type="tel"
 *   mask={phoneMask}
 *   maskPlaceholder="(123) 456-7890"
 * />
 */
export const FormInput = ({
  name,
  control,
  defaultValue,
  value: valueOverride,
  onChange: onChangeOverride,
  rules,
  label,
  placeholder,
  required,
  disabled,
  type,
  icon,
  iconPosition,
  fluid,
  maxLength,
  displayFormat,
  displayMode,
  conversionContext,
  mask,
  maskPlaceholder,
  guide,
  prefix,
  suffix,
  'data-testid': dataTestId,
  'data-automation-id': dataAutomationId,
}) => {
  const {
    value: rhfValue,
    onChange: rhfOnChange,
    onBlur,
    name: fieldName,
    ref,
    error,
    errorMessage,
    mask: maskValue,
    maskPlaceholder: maskPlaceholderValue,
    prefix: prefixValue,
    suffix: suffixValue,
  } = useFormInput({
    name,
    control,
    defaultValue,
    rules,
    type,
    displayFormat,
    displayMode,
    conversionContext,
    mask,
    maskPlaceholder,
    prefix,
    suffix,
  })

  return (
    <FormInputView
      name={fieldName}
      value={valueOverride !== undefined ? valueOverride : rhfValue}
      onChange={onChangeOverride || rhfOnChange}
      onBlur={onBlur}
      inputRef={ref}
      label={label}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      type={type}
      icon={icon}
      iconPosition={iconPosition}
      fluid={fluid}
      maxLength={maxLength}
      error={error}
      errorMessage={errorMessage}
      mask={maskValue}
      maskPlaceholder={maskPlaceholderValue}
      guide={guide}
      prefix={prefixValue}
      suffix={suffixValue}
      data-testid={dataTestId}
      data-automation-id={dataAutomationId}
    />
  )
}

FormInput.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  rules: PropTypes.object,
  label: PropTypes.node,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(['text', 'number', 'email', 'password', 'tel', 'url']),
  icon: PropTypes.string,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  fluid: PropTypes.bool,
  maxLength: PropTypes.number,
  displayFormat: PropTypes.oneOf(['number', 'currency', 'date']),
  displayMode: PropTypes.oneOf(['currency', 'percent']),
  conversionContext: PropTypes.object,
  mask: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  maskPlaceholder: PropTypes.string,
  guide: PropTypes.bool,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  'data-testid': PropTypes.string,
  'data-automation-id': PropTypes.string,
}

FormInput.defaultProps = {
  type: 'text',
  fluid: true,
  disabled: false,
  required: false,
  defaultValue: undefined,
}
